import { useEffect, useMemo, useState } from 'react'
import BlogCard from './BlogCard'
import { Button } from '@/common/ui/button'
import { Search } from 'lucide-react'
import { useFetchBlogs } from './blogQueries'
import { H1 } from '@/common/ui'
import BlogFilterAppliedAnalytics from '@/common/analytics/events/BlogFilterAppliedAnalytics'
import { Helmet } from 'react-helmet'
import { APP } from '@/common/strings'
import { BLOGS_COPY } from '@/locales/en'

export function BlogsPage(): JSX.Element {
    const [query, setQuery] = useState('')
    const [activeCategory, setActiveCategory] = useState('All')

    const { data, isLoading } = useFetchBlogs()

    const filtered = useMemo(() => {
        return (
            data?.blogs?.filter((b) => {
                if (activeCategory !== 'All') {
                    if (b.category !== activeCategory) return false
                }
                if (!query) return true
                const q = query.toLowerCase()
                return b.title.toLowerCase().includes(q) || (b.summary || '').toLowerCase().includes(q)
            }) || []
        )
    }, [data, query, activeCategory])

    useEffect(() => {
        if (activeCategory !== 'All' || query) {
            BlogFilterAppliedAnalytics.trackDebounced(
                'blog-filter-applied',
                {
                    ...(activeCategory !== 'All' && { category: activeCategory }),
                    ...(query && { query }),
                },
                1000,
            )
        }
    }, [activeCategory, query])

    const categories = useMemo(() => {
        const acc = ['All']
        const blogs = data?.blogs || []
        blogs.forEach((b) => {
            if (b.category && !acc.includes(b.category)) {
                acc.push(b.category)
            }
        })
        return acc
    }, [data])

    return (
        <div>
            <Helmet>
                <title>
                    {BLOGS_COPY.title} | {APP.title}
                </title>
            </Helmet>

            <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
                <div className="flex flex-col justify-start items-start gap-4">
                    <div className="self-stretch flex flex-col justify-start items-start gap-4">
                        <div className="self-stretch inline-flex justify-start items-center gap-2">
                            <H1 className="justify-center text-black text-4xl font-normal font-['Tangerine'] leading-10 mb-0">
                                {BLOGS_COPY.header}
                            </H1>
                        </div>
                    </div>
                </div>

                <div
                    data-size="Default"
                    data-state="Default"
                    className="w-[250px] h-10 flex flex-col justify-start items-start gap-1.5"
                >
                    <div className="w-full self-stretch flex-1 inline-flex justify-start items-start gap-2">
                        <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-1.5">
                            <div className="w-full flex-1 px-4 py-1 bg-white rounded-[100px] outline outline-1 outline-neutral-300 inline-flex justify-start items-center gap-3">
                                <Search className="text-brand-forest" size={24} />
                                <label htmlFor="blog-search" className="sr-only">
                                    {BLOGS_COPY.searchAriaLabel}
                                </label>
                                <input
                                    id="blog-search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder={BLOGS_COPY.searchPlaceholder}
                                    aria-label={BLOGS_COPY.searchAriaLabel}
                                    className="w-full text-content-tertiary text-sm font-normal font-['Inter'] leading-5 outline-none"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="relative w-full h-10">
                    <div className="absolute -inset-x-6 z-50 pointer-events-auto overflow-x-auto overflow-y-hidden pb-2">
                        <div className="w-full px-6 self-stretch relative">
                            <div className="flex items-start gap-4 min-w-max whitespace-nowrap">
                                {categories.map((c) => (
                                    <Button
                                        size="sm"
                                        variant={activeCategory === c ? 'secondary' : 'outline'}
                                        onClick={() => setActiveCategory(c)}
                                        className={`px-3 py-1.5 flex-shrink-0`}
                                    >
                                        <div className="justify-center text-sm font-medium font-['Inter'] leading-5">
                                            {c}
                                        </div>
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider" />

                {isLoading && (
                    <div className="self-stretch grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div className="w-full" key={i}>
                                <BlogCard />
                            </div>
                        ))}
                    </div>
                )}

                {!isLoading && filtered.length === 0 && (
                    <div className="flex flex-col justify-start items-start gap-4">
                        <div className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                            {BLOGS_COPY.noResultsTitle}
                        </div>
                        <div className="justify-start text-black text-sm font-normal font-['Inter'] leading-5">
                            {BLOGS_COPY.noResultsDescription}
                        </div>
                    </div>
                )}

                {!isLoading && filtered.length > 0 && (
                    <>
                        <div className="justify-start text-black text-base font-medium font-['Inter'] leading-6">
                            {BLOGS_COPY.blogsAvailable.replace('{count}', filtered.length.toString())}
                        </div>

                        <div className="self-stretch grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {filtered.map((b) => (
                                <div className="w-full" key={b.slug}>
                                    <BlogCard blog={b} />
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

export default BlogsPage
